import * as React from 'react';
import {SliderConstants} from './constants';
import {useWindowEventListener} from '../../utils/use-window-event-listener';

// This file is adapted from the components-core version for the slider

interface ThumbKeyHandlers {
  onKeyUp: React.KeyboardEventHandler;
  onKeyDown: React.KeyboardEventHandler;
}

export function useThumbKeyHandlers(
  onSlidingChange: (sliding: boolean) => void,
  onValueChange: (update: (old: number) => number) => void,
  isRtl: boolean
): ThumbKeyHandlers {
  // The hardest part for the keyboard handlers is to find out when we stopped
  // sliding.
  // We're using two mechanisms here:
  // 1. A set of all (relevant) keys that are currently pressed, once it's empty
  //    the user stopped sliding. (fires immediately)
  // 2. As a failsafe in case we e.g. somehow missed a keyup event, we also set
  //    a timout that fires if no (relevant) key was pressed in the last 600ms.
  //    This will have a little delay, we usually expect 1. to work.

  // For method 1: The Set of pressed keys
  const pressedKeys = React.useRef(new Set<string>());

  // For method 2: The timeout
  const undoSlidingTimeout = React.useRef<number | undefined>(undefined);

  const move = React.useCallback(
    (increment: number) => (old: number) =>
      old + increment * SliderConstants.STEP,
    []
  );

  const handleSlideEnd = React.useCallback(() => {
    onSlidingChange(false);
    window.clearTimeout(undoSlidingTimeout.current);
    pressedKeys.current.clear();
  }, [onSlidingChange]);

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      let change: ((old: number) => number) | undefined;

      switch (event.key) {
        case 'ArrowLeft':
          change = move(isRtl ? 1 : -1);
          break;

        case 'ArrowDown':
          change = move(-1);
          break;

        case 'ArrowRight':
          change = move(isRtl ? -1 : 1);
          break;

        case 'ArrowUp':
          change = move(1);
          break;

        case 'PageDown':
          change = move(-10);
          break;

        case 'PageUp':
          change = move(10);
          break;

        case 'Home':
          change = () => SliderConstants.MIN;
          break;

        case 'End':
          change = () => SliderConstants.MAX;
          break;

        default:
          return;
      }

      pressedKeys.current.add(event.key);
      window.clearTimeout(undoSlidingTimeout.current);
      // safety net if no keyup event fired
      undoSlidingTimeout.current = window.setTimeout(handleSlideEnd, 600);
      onSlidingChange(true);
      onValueChange(change);

      event.preventDefault();
      event.stopPropagation();
    },
    [handleSlideEnd, onSlidingChange, onValueChange, move, isRtl]
  );

  const onKeyUp = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (pressedKeys.current.size > 0) {
        pressedKeys.current.delete(event.key);
        if (pressedKeys.current.size === 0) {
          handleSlideEnd();
        }
      }
    },
    [handleSlideEnd]
  );

  return {onKeyDown, onKeyUp};
}

function getSliderValueByXCoordinate(
  xCoordinate: number,
  railWidth: number
): number {
  return (xCoordinate / railWidth) * 100;
}

export function usePointerDownHandler(
  railElementRef: React.RefObject<HTMLDivElement>,
  thumbElementRef: React.RefObject<HTMLDivElement>,
  onSlidingChange: (sliding: boolean) => void,
  onValueChange: (update: (old: number) => number) => void,
  isRtl: boolean
): React.PointerEventHandler {
  /**
   * We only handle move and up listeners for same pointerId as down, so that
   * multi-touch doesn't break
   */
  const pointerId = React.useRef<number | undefined>();

  const pointerUpListener = useWindowEventListener('pointerup');
  const pointerCancelListener = useWindowEventListener('pointercancel');
  const pointerMoveListener = useWindowEventListener('pointermove');

  const pointerUpOrCancelHandler = React.useCallback(
    (event: PointerEvent) => {
      if (event.pointerId === pointerId.current) {
        onSlidingChange(false);
        // trigger onChange
        onValueChange((old) => old);
        pointerUpListener.unregister();
        pointerMoveListener.unregister();
        pointerCancelListener.unregister();
        pointerId.current = undefined;
      }
    },
    [
      onSlidingChange,
      onValueChange,
      pointerUpListener,
      pointerMoveListener,
      pointerCancelListener,
    ]
  );

  const pointerMoveHandler = React.useCallback(
    (event: PointerEvent) => {
      if (event.pointerId === pointerId.current && railElementRef.current) {
        const {left, right} = railElementRef.current.getBoundingClientRect();
        const diffX = isRtl ? right - event.pageX : event.pageX - left;

        const railWidth = railElementRef.current.clientWidth;
        onValueChange(() => getSliderValueByXCoordinate(diffX, railWidth));

        event.preventDefault();
        event.stopPropagation();
      }
    },
    [isRtl, onValueChange, railElementRef]
  );

  return React.useCallback(
    (event: React.PointerEvent) => {
      onSlidingChange(true);

      event.stopPropagation();

      thumbElementRef.current?.focus();

      pointerId.current = event.pointerId;

      // pointer move and up listeners should be on whole window, so state doesn't get stuck
      // when pointer moves away from element, while being pressed.
      pointerUpListener.register(pointerUpOrCancelHandler);
      pointerCancelListener.register(pointerUpOrCancelHandler);
      pointerMoveListener.register(pointerMoveHandler);
    },
    [
      onSlidingChange,
      thumbElementRef,
      pointerUpListener,
      pointerUpOrCancelHandler,
      pointerCancelListener,
      pointerMoveListener,
      pointerMoveHandler,
    ]
  );
}
